import { type MaterialStatus, MaterialType } from '@mnd-frontend/graphql-schema';
import type { MaterialTypeUnion } from '@mnd-frontend/ts';
import routes from '../../routes';

type MaterialStatusUnion = `${MaterialStatus}`;

export function getMaterialTypePathname(type: MaterialTypeUnion) {
  switch (type) {
    case 'Pressrelease':
      return 'pressreleases';
    case 'BlogPost':
      return 'blog_posts';
    case 'Event':
      return 'events';
    case 'Video':
      return 'videos';
    case 'News':
      return 'news';
    case 'Document':
      return 'documents';
    case 'Image':
      return 'images';
  }
}

export enum MaterialCta {
  Preview = 0,
  Edit = 1,
  MediaHits = 2,
  AnalyzeOverview = 3,
  AnalyzeMaterial = 4,
}

type Item = {
  [key: string]: unknown;
  __typename: MaterialTypeUnion;
  id: string;
  status?: MaterialStatusUnion;
};

export const getMaterialCta = ({
  item,
  hasAnalyzeEnabled,
  hasMediaHitsAccess,
  hasAnalyzeMaterial,
}: {
  item: Item;
  hasAnalyzeEnabled: boolean;
  hasMediaHitsAccess: boolean;
  hasAnalyzeMaterial: boolean;
}): MaterialCta => {
  const { status, quotationsCount, previewUrl, __typename } = item;

  if (status === 'archived' && previewUrl) {
    return MaterialCta.Preview;
  }

  if (status !== 'published' || !hasAnalyzeEnabled || typeof quotationsCount !== 'number') {
    return MaterialCta.Edit;
  }

  if (!hasAnalyzeMaterial) {
    return MaterialCta.AnalyzeOverview;
  }
  if (!hasMediaHitsAccess) {
    return MaterialCta.AnalyzeMaterial;
  }

  switch (__typename) {
    case MaterialType.Pressrelease:
    case MaterialType.BlogPost:
    case MaterialType.News:
      return MaterialCta.MediaHits;
    default:
      return MaterialCta.AnalyzeMaterial;
  }
};

export function getItemPathname({
  item,
  pressroomId,
  hasAnalyzeEnabled,
  hasMediaHitsAccess,
  hasAnalyzeMaterial,
}: {
  item: Item;
  pressroomId: string;
  hasAnalyzeEnabled: boolean;
  hasMediaHitsAccess: boolean;
  hasAnalyzeMaterial: boolean;
}): string {
  const { id, previewUrl, __typename } = item;
  const materialType = getMaterialTypePathname(__typename);

  const action = getMaterialCta({
    item,
    hasAnalyzeEnabled,
    hasAnalyzeMaterial,
    hasMediaHitsAccess,
  });
  switch (action) {
    case MaterialCta.Edit:
      return materialType === 'images'
        ? routes.publishImagesEdit({
            pressroomId,
            ids: id,
          })
        : routes.publishEdit({
            pressroomId,
            materialType,
            id,
          });
    case MaterialCta.Preview:
      return previewUrl as string;
    case MaterialCta.AnalyzeOverview:
      return routes.analyzeOverview({ pressroomId });
    case MaterialCta.AnalyzeMaterial:
      return routes.analyzeMaterial({ id, materialType, pressroomId });
    case MaterialCta.MediaHits:
      return routes.analyzeMaterialTab({
        pressroomId,
        materialType,
        id,
        tab: 'media_hits',
      });
  }
}
